import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Telestrator } from "./Telestrator";
import {
  Play, Pause, SkipBack, SkipForward, Rewind, FastForward,
  Maximize, Minimize, Volume2, VolumeX, Loader2, Goal, Flag,
} from "lucide-react";

const SEEK_STEP = 5;

function fmt(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Plays two synced angles (endzone + sideline). Click an angle to enlarge it;
// click it again (or the other) to change focus. Transport controls both in sync.
export function DualClipPlayer({ play, urls, loading, onPrev, onNext, hasPrev, hasNext }) {
  const ezRef = useRef(null);
  const slRef = useRef(null);
  const resumeRef = useRef(null);
  const [focused, setFocused] = useState(null); // null = equal split
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [fs, setFs] = useState(false);

  const isOff = play.unit === "offense";
  const master = () => ezRef.current || slRef.current;
  const both = () => [ezRef.current, slRef.current].filter(Boolean);

  const togglePlay = useCallback(() => {
    const m = master();
    if (!m) return;
    if (m.paused) both().forEach((v) => v.play().catch(() => {}));
    else both().forEach((v) => v.pause());
  }, []);

  const seekTo = (t) => both().forEach((v) => { try { v.currentTime = t; } catch {} });
  const seekBy = (d) => { const m = master(); if (m) seekTo(Math.max(0, Math.min(m.duration || 0, m.currentTime + d))); };
  const onSeekInput = (e) => { const m = master(); if (m) { const t = (Number(e.target.value) / 100) * (m.duration || 0); seekTo(t); setCurrent(t); } };

  const toggleMute = () => { const next = !muted; setMuted(next); both().forEach((v) => (v.muted = next)); };

  const toggleFullscreen = () => {
    const m = master();
    if (m) resumeRef.current = { time: m.currentTime, playing: !m.paused, muted };
    setFs((x) => !x);
  };

  useEffect(() => {
    if (!fs) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") toggleFullscreen(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fs]);

  // keep secondary angle synced to master
  const onMasterTime = (e) => {
    setCurrent(e.target.currentTime);
    const m = ezRef.current, s = slRef.current;
    if (m && s && Math.abs(s.currentTime - m.currentTime) > 0.35) {
      try { s.currentTime = m.currentTime; } catch {}
    }
  };

  const onLoadedMeta = (isMaster) => (e) => {
    if (isMaster) setDuration(e.target.duration || 0);
    const r = resumeRef.current;
    if (r) {
      try { e.target.currentTime = r.time; } catch {}
      e.target.muted = r.muted;
      if (r.playing) e.target.play().catch(() => {});
    }
  };
  useEffect(() => { const t = setTimeout(() => (resumeRef.current = null), 1500); return () => clearTimeout(t); }, [fs]);

  const pct = duration > 0 ? (current / duration) * 100 : 0;
  const hasEz = !!play.endzone, hasSl = !!play.sideline;

  const AngleTile = ({ view, vref, label, Icon }) => {
    const grow = focused === null ? 1 : focused === view ? 3 : 1;
    const url = urls[view];
    return (
      <div data-testid={`dual-view-${view}`} onClick={() => setFocused((f) => (f === view ? null : view))}
        style={{ flexGrow: grow, flexBasis: 0 }}
        className="relative h-full min-w-0 cursor-pointer transition-[flex-grow] duration-300 ease-out overflow-hidden bg-black">
        {url ? (
          <video ref={vref} src={url} playsInline
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
            onTimeUpdate={view === (hasEz ? "endzone" : "sideline") ? onMasterTime : undefined}
            onLoadedMetadata={onLoadedMeta(view === (hasEz ? "endzone" : "sideline"))}
            onEnded={() => (hasNext ? onNext() : setPlaying(false))}
            className="absolute inset-0 h-full w-full object-contain" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        )}
        <div className={`absolute top-2 left-2 z-20 flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-widest ${focused === view ? "bg-primary text-white" : "bg-black/70 text-white"}`}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.75} /> {label}
        </div>
      </div>
    );
  };

  const Btn = ({ onClick, disabled, testid, size = "md", children }) => {
    const dims = size === "lg" ? "h-14 w-14" : "h-11 w-11";
    const tone = size === "lg" ? "bg-primary hover:bg-red-500 text-white" : "bg-white/10 hover:bg-white/25 text-white backdrop-blur-sm";
    return (
      <button data-testid={testid} onClick={onClick} disabled={disabled}
        className={`${dims} ${tone} flex items-center justify-center rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}>
        {children}
      </button>
    );
  };

  const PlayIcon = playing
    ? <Pause className="h-7 w-7 fill-current" strokeWidth={1} />
    : <Play className="h-7 w-7 fill-current ml-0.5" strokeWidth={1} />;

  const Inner = (
    <>
      {/* angle tiles */}
      <div className="absolute inset-0 flex gap-1 p-1">
        {hasSl && <AngleTile view="sideline" vref={slRef} label="Sideline" Icon={Flag} />}
        {hasEz && <AngleTile view="endzone" vref={ezRef} label="Endzone" Icon={Goal} />}
      </div>

      <Telestrator key={`${play.play_number}-${fs}`} />

      {/* metadata */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/75 backdrop-blur-sm rounded-full px-3.5 py-1.5 border border-white/10">
        <span className={`font-heading text-sm font-black tabular-nums ${isOff ? "text-primary" : "text-secondary"}`}>#{play.play_number}</span>
        <span className="h-3 w-px bg-white/25" />
        <span className="text-xs font-bold uppercase tracking-widest text-white">Q{play.quarter}</span>
        <span className="h-3 w-px bg-white/25" />
        <span className={`text-xs font-bold uppercase tracking-widest ${isOff ? "text-primary" : "text-secondary"}`}>{play.unit}</span>
        <span className="h-3 w-px bg-white/25" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-success">2 angles</span>
      </div>

      {/* controls */}
      <div className="absolute bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-3 pt-8">
        <div className="flex items-center gap-3 mb-2.5">
          <span className="text-[11px] font-bold tabular-nums text-white/90 w-10 text-right">{fmt(current)}</span>
          <input type="range" min={0} max={100} step={0.1} value={pct} onChange={onSeekInput} data-testid="dual-seek" aria-label="Seek"
            className="flex-1 h-1.5 appearance-none rounded-full cursor-pointer accent-primary"
            style={{ background: `linear-gradient(to right, #FF3B30 ${pct}%, rgba(255,255,255,0.25) ${pct}%)` }} />
          <span className="text-[11px] font-bold tabular-nums text-white/60 w-10">{fmt(duration)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3">
            <Btn testid="dual-skip-back" onClick={onPrev} disabled={!hasPrev}><SkipBack className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="dual-rewind" onClick={() => seekBy(-SEEK_STEP)}><Rewind className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="dual-playpause" onClick={togglePlay} size="lg">{PlayIcon}</Btn>
            <Btn testid="dual-forward" onClick={() => seekBy(SEEK_STEP)}><FastForward className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="dual-skip-forward" onClick={onNext} disabled={!hasNext}><SkipForward className="h-5 w-5" strokeWidth={2.5} /></Btn>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="dual-mute" onClick={toggleMute} className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
              {muted ? <VolumeX className="h-5 w-5" strokeWidth={2.5} /> : <Volume2 className="h-5 w-5" strokeWidth={2.5} />}
            </button>
            <button data-testid="dual-fullscreen" onClick={toggleFullscreen} className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
              {fs ? <Minimize className="h-5 w-5" strokeWidth={2.5} /> : <Maximize className="h-5 w-5" strokeWidth={2.5} />}
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>}
    </>
  );

  if (fs) {
    return createPortal(
      <div data-testid="dual-player-wrap" className="fixed inset-0 z-[9999] bg-black w-screen h-[100dvh]">{Inner}</div>,
      document.body
    );
  }
  return <div data-testid="dual-player-wrap" className="relative bg-black w-full aspect-video">{Inner}</div>;
}
